/**
 * @fileoverview Where a creditor ranks — secured, preferred, or unsecured.
 *
 * Purpose: the register stores two independent facts (`secured`, derived from
 * what the row is linked to, and `preferential`, set by the practitioner), but
 * a practitioner reads one ranking. This is the single place that collapses
 * the two into that ranking and back, so the table, the add form and the
 * detail panel can never disagree about what a row is.
 *
 * Order matters: a debt secured over property is paid out of that property
 * before the statutory preference ever applies, so secured wins where a row is
 * both.
 */

import type { BadgeColor } from "@/ui/primitives/badge";

export type CreditorRanking = "secured" | "preferential" | "unsecured";

export const RANKING_LABELS: Record<CreditorRanking, string> = {
  secured: "Secured",
  preferential: "Preferred",
  unsecured: "Unsecured",
};

export const RANKING_BADGE_COLORS: Record<CreditorRanking, BadgeColor> = {
  secured: "amber",
  preferential: "violet",
  unsecured: "gray",
};

export const RANKING_OPTIONS = [
  { value: "unsecured", label: RANKING_LABELS.unsecured },
  { value: "preferential", label: RANKING_LABELS.preferential },
  { value: "secured", label: RANKING_LABELS.secured },
] as const;

/** Statutory order — what gets paid first sorts first. */
export const RANKING_GROUP_ORDER = [
  "secured",
  "preferential",
  "unsecured",
] as const satisfies readonly CreditorRanking[];

export const RANKING_SORT_ORDER: Record<CreditorRanking, number> = {
  secured: 0,
  preferential: 1,
  unsecured: 2,
};

/** How a row ranks, from the two flags stored on it. */
export function creditorRanking(entry: {
  secured?: boolean;
  preferential?: boolean;
}): CreditorRanking {
  if (entry.secured) return "secured";
  if (entry.preferential) return "preferential";
  return "unsecured";
}

/** The flags to store for a ranking the practitioner picked. */
export function rankingToFlags(ranking: CreditorRanking): {
  secured: boolean;
  preferential: boolean;
} {
  return {
    secured: ranking === "secured",
    preferential: ranking === "preferential",
  };
}
